import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";

import {
  Card,
  PageHeading,
  Stack,
  Table,
} from "~/components";
import { queryClient } from "~/lib/queryClient";
import { currentUserQueryOptions } from "~/lib/auth";
import {
  USERS_LIST_KEY,
  useUpdateUserRole,
  usersListQueryOptions,
  type User,
  type UsersCollection,
} from "~/lib/users";
import { TASK_ROLES } from "~/lib/workflows";

import * as css from "~/routes/_app.users.css";

// /users — admin surface for assigning firm roles. v1 is intentionally
// minimal: list every user, change role inline. The role drives the
// priority queue's routing, so this is the page you visit when a new
// hire shows up or someone changes desks.
export const Route = createFileRoute("/_app/users")({
  component: UsersPage,
  loader: () => queryClient.ensureQueryData(usersListQueryOptions),
});

function UsersPage() {
  const usersQuery = useQuery(usersListQueryOptions);
  const { data: me } = useSuspenseQuery(currentUserQueryOptions);

  const heading = (
    <PageHeading
      title="Users"
      subtitle="Assign firm roles. The role you set here is what the priority queue routes work to."
    />
  );

  if (usersQuery.isPending) {
    return (
      <Stack gap="lg">
        {heading}
        <Card>
          <p>Loading users…</p>
        </Card>
      </Stack>
    );
  }

  // The system user is excluded server-side via the email_ne filter
  // (see usersListQueryOptions), so the roster here is firm members only.
  const users = usersQuery.data?._embedded ?? [];

  if (users.length === 0) {
    return (
      <Stack gap="lg">
        {heading}
        <Card>
          <p className={css.emptyState}>No users yet.</p>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {heading}
      <Card>
        <Table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Auth tier</th>
              <th>Firm role</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <UserRow key={user.id} user={user} isMe={user.id === me.id} />
            ))}
          </tbody>
        </Table>
      </Card>
    </Stack>
  );
}

function UserRow({ user, isMe }: { user: User; isMe: boolean }) {
  const queryClient = useQueryClient();
  const mutation = useUpdateUserRole();

  // Optimistic update — flip the row's role in the cached users list
  // immediately. The mutation invalidates on success so the canonical
  // value catches up; rollback on error restores the previous role.
  const handleChange = (nextRole: string) => {
    if (nextRole === user.role) return;
    const prev = queryClient.getQueryData<UsersCollection>(USERS_LIST_KEY);
    queryClient.setQueryData<UsersCollection>(USERS_LIST_KEY, (old) => {
      if (old === undefined) return old;
      return {
        ...old,
        _embedded: (old._embedded ?? []).map((u) =>
          u.id === user.id ? { ...u, role: nextRole } : u,
        ),
      };
    });
    mutation.mutate(
      { id: user.id, role: nextRole },
      {
        onError: () => {
          if (prev !== undefined) queryClient.setQueryData(USERS_LIST_KEY, prev);
        },
      },
    );
  };

  const inFlight = mutation.isPending && mutation.variables?.id === user.id;
  const fullName = formatName(user);

  return (
    <tr>
      <td>
        <span className={css.userName}>{fullName}</span>
        {isMe && <span className={css.youBadge}>you</span>}
      </td>
      <td className={css.meta}>{user.email}</td>
      <td className={css.meta}>{user.type}</td>
      <td>
        <select
          className={css.roleSelect}
          value={user.role}
          onChange={(e) => handleChange(e.target.value)}
          disabled={inFlight}
          aria-label={`Firm role for ${fullName}`}
        >
          <option value="">— unassigned —</option>
          {TASK_ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        {mutation.isError && mutation.variables?.id === user.id && (
          <div className={css.errorCell}>
            Couldn't save: {(mutation.error as Error).message}
          </div>
        )}
      </td>
    </tr>
  );
}

function formatName(user: User): string {
  const first = user.first_name?.trim() ?? "";
  const last = user.last_name?.trim() ?? "";
  const full = `${first} ${last}`.trim();
  return full.length > 0 ? full : user.email;
}
